import { useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { UNDO_WINDOW_MS, useUndoState } from '@/lib/undo'

/**
 * "Deleted Book Haircut. Undo."
 *
 * One notice at the shell level rather than per screen, because the actions it
 * covers — delete, archive, discard, park — live on six different pages and
 * mean the same thing on all of them.
 *
 * Sits where `SaveNotice` sits and looks the same, which is deliberate: they
 * are both the app speaking after the fact, and giving them different
 * treatments would imply a difference in importance that is not there.
 */
export function UndoNotice() {
  const offer = useUndoState((state) => state.offer)
  const dismiss = useUndoState((state) => state.dismiss)

  useEffect(() => {
    if (offer === null) return
    const { id } = offer
    const timer = setTimeout(() => dismiss(id), UNDO_WINDOW_MS)
    return () => clearTimeout(timer)
    // Keyed on the id so a second action restarts the window rather than
    // inheriting whatever was left of the first one's.
  }, [offer?.id, offer, dismiss])

  if (offer === null) return null

  return (
    <div
      role="status"
      className="pb-safe fixed inset-x-0 bottom-16 z-50 flex justify-center px-4 sm:bottom-4"
    >
      <div className="bg-foreground text-background flex max-w-md items-center gap-3 rounded-md px-3 py-2 text-sm shadow-lg">
        <span className="flex-1">{offer.message}</span>
        <Button
          type="button"
          size="sm"
          variant="secondary"
          className="min-h-8 shrink-0"
          onClick={() => {
            const { id } = offer
            offer.undo()
            // By id, because `undo` may have presented a fresh offer — the way
            // back from the way back. Clearing blind took it with it.
            dismiss(id)
          }}
        >
          Undo
        </Button>
      </div>
    </div>
  )
}
